"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  number: "01",
  name: "Roof Inspections",
  tagline: "thorough structural assessment",
  description: "Our certified experts conduct a comprehensive evaluation of your roofing system, identifying potential vulnerabilities, storm damage, or wear before they escalate into costly interior repairs.",
  price: "FROM $150",
  duration: "1 DAY"
}, {
  number: "02",
  name: "Roof Repairs",
  tagline: "precision leak mitigation",
  description: "We provide rapid, high-quality repair services for shingles, flashing, and underlayment. Our team ensures your home remains protected against the elements with durable, long-lasting solutions.",
  price: "FROM $400",
  duration: "2 DAYS"
}, {
  number: "03",
  name: "Roof Replacements",
  tagline: "premium material installation",
  description: "Transform your property with a full roof replacement. We utilize top-tier materials and industry-leading installation techniques to enhance your home's value and weather resistance.",
  price: "FROM $8,000",
  duration: "5 DAYS"
}, {
  number: "04",
  name: "Storm Damage Repair",
  tagline: "emergency restoration services",
  description: "When severe weather strikes, our rapid-response team is ready to secure your property, mitigate further damage, and restore your roof to its original, pristine condition.",
  price: "FROM $600",
  duration: "3 DAYS"
}, {
  number: "05",
  name: "Insurance Assistance",
  tagline: "claims advocacy support",
  description: "Navigating insurance claims can be complex. We provide detailed documentation, professional assessments, and direct communication with adjusters to ensure you receive fair coverage.",
  price: "COMPLIMENTARY",
  duration: "ONGOING"
}];
export default function Roofing() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5052" id="our-roofing-solutions" className="bg-background text-foreground pt-[200px] pb-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-[1440px] mx-auto">
        <div ref={headerRef} className={`grid grid-cols-12 gap-6 mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="col-span-12 flex items-center justify-between mb-16">
            <nav aria-label="breadcrumb" className="font-mono text-xs tracking-[0.2em] text-muted-foreground">
              <ol className="flex items-center gap-2">
                <li>
                  <Link to="/HomePage" className="hover:text-primary transition-colors duration-300">
                    HOME
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-foreground">SERVICES</li>
              </ol>
            </nav>
            <span className="font-mono text-xs tracking-[0.2em] text-primary">
              LUXURY CONSTRUCTION
            </span>
          </div>

          <h1 className="col-span-12 lg:col-span-9 font-default font-bold text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] tracking-tighter">
            Professional Roofing <em className="italic text-primary">Solutions</em> for Your <em className="italic text-primary">Property</em>
          </h1>

          <p className="col-span-12 lg:col-span-6 lg:col-start-1 mt-12 text-muted-foreground text-base md:text-lg leading-relaxed font-default">
            Luxury Construction LLC provides reliable, high-quality roofing services across Southwest Kansas. From routine inspections to full replacements, our team ensures your home is protected with expert craftsmanship.
          </p>
        </div>

        <div className="border-t border-border">
          {services.map((service, i) => <ServiceRow key={service.number} service={service} i={i} />)}
        </div>

        <div className="mt-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3 text-center">
            <p className="font-mono text-xs tracking-[0.2em] text-muted-foreground mb-6">
              GET A FREE QUOTE
            </p>
            <p className="font-default text-2xl md:text-3xl font-semibold leading-relaxed mb-10">
              Ready to protect your home? Contact our team today for a professional inspection and a fair, transparent estimate.
            </p>
            <Button asChild variant="outline" size="lg" className="font-mono text-xs tracking-[0.2em] border-primary bg-primary text-primary-foreground hover:bg-primary/90 px-8 h-12">
              <Link to="/ContactUsPage">
                REQUEST CONSULTATION
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
type Service = {
  number: string;
  name: string;
  tagline: string;
  description: string;
  price: string;
  duration: string;
};
function ServiceRow({
  service,
  i
}: {
  service: Service;
  i: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div data-index={i} ref={ref} className={`group relative border-b border-border transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${i * 80}ms`
  }}>
      <div className="absolute inset-0 bg-muted/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="relative grid grid-cols-12 gap-4 md:gap-6 py-12 md:py-16 transition-colors duration-500 group-hover:text-primary">
        <div className="col-span-3 md:col-span-1">
          <span className="font-default font-bold text-5xl md:text-6xl lg:text-7xl xl:text-[80px] leading-none text-primary block">
            {service.number}
          </span>
        </div>

        <div className="col-span-9 md:col-span-4 flex flex-col justify-start md:pl-4">
          <h3 className="font-default font-semibold text-2xl md:text-3xl lg:text-4xl leading-tight mb-2 text-foreground transition-colors duration-500 group-hover:text-primary">
            {service.name}
          </h3>
          <p className="font-default italic text-base md:text-lg text-muted-foreground">
            {service.tagline}
          </p>
        </div>

        <div className="col-span-12 md:col-span-5 flex items-start">
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed font-default">
            {service.description}
          </p>
        </div>

        <div className="col-span-12 md:col-span-2 flex flex-row md:flex-col items-start md:items-end justify-between md:justify-start gap-4 md:gap-3">
          <div className="font-mono text-xs tracking-[0.15em] text-foreground md:text-right">
            <div>{service.price}</div>
            <div className="text-muted-foreground mt-1">/ {service.duration}</div>
          </div>
          <Link to="/ContactUsPage" aria-label={`Inquire about ${service.name}`} className="inline-flex items-center justify-center size-10 text-foreground group-hover:text-primary transition-all duration-500">
            <ArrowRight className="size-5 transition-transform duration-500 group-hover:translate-x-2" />
          </Link>
        </div>
      </div>
    </div>;
}